// libraries
import React, { useState, useId } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString, useAnnounceOwnError } from '/lib/reactutils/utils'

// svg
import { Warning, Eye, EyeSlash } from '/react4xp/components/Svg/Ux/Ux'

interface InputProps {
    id: string
    name: string
    label?: string
    type: string
    placeholder?: string
    required?: boolean
    maxLength?: number
    minLength?: number
    value?: string
    disabled?: boolean
    showHidePassword?: boolean
    className?: string
    errorMessages?: {
        required?: string
        maxLength?: string
        minLength?: string
        invalidEmail?: string
    }
    helperText?: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    hidden?: boolean
}

export default function Input({
    id,
    name,
    label,
    type,
    placeholder,
    required,
    maxLength,
    minLength,
    value,
    disabled,
    showHidePassword,
    className,
    errorMessages,
    helperText,
    onChange = () => {},
    onKeyDown,
    hidden
}: InputProps) {

    const formContext = useFormContext()
    const register = formContext ? formContext.register : () => ({})
    const errors = formContext ? formContext.formState.errors : {}

    let validations: RegisterOptions = {
        onChange: (e) => onChange(e),
        required
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    if (maxLength) {
        validations.maxLength = {
            value: maxLength,
            message: errorMessages?.maxLength
        }
    }

    if (minLength) {
        validations.minLength = {
            value: minLength,
            message: errorMessages?.minLength
        }
    }

    // add pattern validation for email type
    if (type === 'email') {
        validations.pattern = {
            value: /\S+@\S+\.\S+/,
            message: errorMessages?.invalidEmail
        }
    }

    const submitCount = formContext ? formContext.formState.submitCount : 0

    const errorMessage = errors && errors[name]?.message
    const announceError = useAnnounceOwnError(Boolean(errorMessage), submitCount)

    const showHelperText = isNonWhitespaceString(helperText)
    const instanceId = useId()
    const helperTextId = `${instanceId}-helper-text`
    const errorMessageId = `${instanceId}-error-message`

    const describedBy = [
        showHelperText && helperTextId,
        errorMessage && errorMessageId
    ].filter(Boolean).join(' ') || undefined

    // show / hide password function
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)

    const togglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible)
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        
        if (type === 'tel') {
            const allowedKeys = /[0-9()+-\s]/

            if (!allowedKeys.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
                event.preventDefault()
            }
        }

        if (event.key === 'Enter' && onKeyDown) {
            onKeyDown(event)
        }
    }

    return (
        <div className={clsx(
            'form-line',
            className,
            errors && errors[name] && 'form-line--error',
            hidden && 'hidden'
        )}>

            {!hidden && (
                <label className='form-line__label text-md' htmlFor={id}>
                    {label} {required && <span className='form-line__label__required text-sm'>(Required)</span>}
                </label>
            )}

            <div className='form-line__line-wrapper'>

                <input
                    type={isPasswordVisible ? 'text' : type}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    className='form-line__input text-md'
                    defaultValue={value}
                    disabled={disabled || false}
                    onKeyDown={handleKeyPress}
                    onChange={onChange}
                    hidden={hidden}
                    aria-hidden={hidden || undefined}
                    tabIndex={hidden ? -1 : undefined}
                    {...(register ? register(name || '', validations) : {})}
                    aria-required={required || undefined}
                    aria-invalid={Boolean(errorMessage)}
                    aria-describedby={describedBy}
                />

                {showHidePassword && (
                    <button
                        type='button'
                        className={clsx(
                            'form-line__side-button show-hide-password',
                            isPasswordVisible && 'is-visible'
                        )}
                        onClick={togglePasswordVisibility}
                    >
                        {isPasswordVisible ? <EyeSlash /> : <Eye />}
                    </button>
                )}

            </div>

            {showHelperText && (
                <div
                    id={helperTextId}
                    className='form-line__help-text text-sm'
                    dangerouslySetInnerHTML={{ __html: helperText }}
                    suppressHydrationWarning
                />
            )}

            {errorMessage && (
                <p
                    id={errorMessageId}
                    role={announceError ? 'alert' : undefined}
                    className='
                        form-line__error-message
                        form-line__error-message--input
                        labels-sm-regular
                    '
                >
                    <Warning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}